import { useRef } from 'react';
import { ELEMENT_SEARCH_CLASSES } from '../constants/classNames';
import '../styles/ElementSearch.css';

/**
 * Search box for the periodic table. Clear it with the ✕ button or the Escape key;
 * Enter picks the best match.
 */
export default function ElementSearch({ query, matchCount, onChangeQuery, onClearQuery, onPickBestMatch }) {
  const inputRef = useRef(null);
  const hasQuery = query.trim().length > 0;

  const clearAndRefocus = () => {
    onClearQuery();
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClearQuery();
    } else if (event.key === 'Enter' && matchCount > 0) {
      event.preventDefault();
      onPickBestMatch();
    }
  };

  const statusText = !hasQuery
    ? ''
    : matchCount === 0
      ? 'No elements found. Try a name, symbol or number.'
      : `${matchCount} found. Press Enter to pick ${matchCount === 1 ? 'it' : 'the first one'}.`;

  return (
    <div className={ELEMENT_SEARCH_CLASSES.root} role="search">
      <div className={ELEMENT_SEARCH_CLASSES.field}>
        <span className={ELEMENT_SEARCH_CLASSES.icon} aria-hidden="true">
          🔍
        </span>
        <input
          ref={inputRef}
          className={ELEMENT_SEARCH_CLASSES.input}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          spellCheck="false"
          placeholder="Find an element: name, symbol or number"
          aria-label="Find an element by name, symbol or number"
          aria-describedby="element-search-status"
          value={query}
          onChange={(event) => onChangeQuery(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        {hasQuery && (
          <button type="button" className={ELEMENT_SEARCH_CLASSES.clearButton} aria-label="Clear search" onClick={clearAndRefocus}>
            ✕
          </button>
        )}
      </div>
      <p id="element-search-status" className={ELEMENT_SEARCH_CLASSES.status} aria-live="polite">
        {statusText}
      </p>
    </div>
  );
}
